(function () {
  'use strict';

  var CART_EVENT = 'tayeb:cart-updated';
  var refreshTimer;
  var debounceTimer;
  var lastUpdateTime = 0;
  var DEBOUNCE_DELAY = 300; // Reduced from multiple delays to single debounce

  function cartUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + path.replace(/^\//, '');
  }

  function announceCartUpdate() {
    var now = Date.now();
    // Debounce to prevent excessive updates
    if (now - lastUpdateTime < DEBOUNCE_DELAY) {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(function () {
        lastUpdateTime = Date.now();
        window.dispatchEvent(new CustomEvent(CART_EVENT));
      }, DEBOUNCE_DELAY);
    } else {
      lastUpdateTime = now;
      window.dispatchEvent(new CustomEvent(CART_EVENT));
    }
  }

  function installCartObserver() {
    if (window.__tayebCartObserverInstalled) return;
    window.__tayebCartObserverInstalled = true;

    // Use event delegation instead of method overriding
    var cartEvents = [
      'cart:updated',
      'cart:change',
      'cart:refresh',
      'cart-drawer:refresh',
      'product:added',
      'ajaxProduct:added'
    ];

    cartEvents.forEach(function (eventName) {
      document.addEventListener(eventName, announceCartUpdate, { passive: true });
    });

    // Use event delegation for form submissions
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.matches) return;

      if (form.matches('form[action*="/cart/add"], form[action*="/cart/change"], form[action*="/cart/update"]')) {
        announceCartUpdate();
      }
    }, { passive: true, capture: true });

    // Use event delegation for cart buttons
    document.addEventListener('click', function (event) {
      var button = event.target && event.target.closest
        ? event.target.closest(
          '[name="add"], [data-add-to-cart], .add-to-cart-button, ' +
          '[data-cart-remove], cart-remove-button, quantity-input button'
        )
        : null;

      if (button) announceCartUpdate();
    }, { passive: true, capture: true });

    // Use event delegation for quantity changes
    document.addEventListener('change', function (event) {
      var field = event.target;
      if (field && field.matches && field.matches(
        'input[name="updates[]"], input[name="quantity"], select[name="quantity"]'
      )) {
        announceCartUpdate();
      }
    }, { passive: true, capture: true });
  }

  function normalizeList(value) {
    return (value || '')
      .split(',')
      .map(function (item) { return item.trim().toLowerCase(); })
      .filter(Boolean);
  }

  function escapeHtml(value) {
    var element = document.createElement('span');
    element.textContent = String(value);
    return element.innerHTML;
  }

  function TayebRewardProgress() {
    return Reflect.construct(HTMLElement, [], TayebRewardProgress);
  }

  TayebRewardProgress.prototype = Object.create(HTMLElement.prototype);
  TayebRewardProgress.prototype.constructor = TayebRewardProgress;
  Object.setPrototypeOf(TayebRewardProgress, HTMLElement);

  TayebRewardProgress.prototype.connectedCallback = function () {
    if (this.__tayebInitialized) return;
    this.__tayebInitialized = true;

    this.target = Math.max(1, parseInt(this.dataset.target, 10) || 5);
    this.isArabic = this.dataset.locale === 'true';
    this.eligibleProductTypes = normalizeList(this.dataset.eligibleProductTypes);
    this.titleElement = this.querySelector('[data-reward-title]');
    this.messageElement = this.querySelector('[data-reward-message]');
    this.fillElement = this.querySelector('[data-reward-fill]');
    this.progressElement = this.querySelector('[data-reward-progress]');
    this.giftElement = this.querySelector('[data-reward-gift]');
    this.ctaElement = this.querySelector('[data-reward-cta]');
    this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-reward-step]'));
    this.handleCartUpdate = this.refresh.bind(this);

    installCartObserver();
    window.addEventListener(CART_EVENT, this.handleCartUpdate);
    window.addEventListener('pageshow', this.handleCartUpdate);
    document.addEventListener('shopify:section:load', this.handleCartUpdate);
    this.refresh();
  };

  TayebRewardProgress.prototype.disconnectedCallback = function () {
    window.removeEventListener(CART_EVENT, this.handleCartUpdate);
    window.removeEventListener('pageshow', this.handleCartUpdate);
    document.removeEventListener('shopify:section:load', this.handleCartUpdate);
  };

  TayebRewardProgress.prototype.isEligible = function (item) {
    var properties = item.properties || {};

    if (properties._tayeb_free_gift === true || properties._tayeb_free_gift === 'true') {
      return false;
    }

    if (properties._tayeb_reward_eligible === true || properties._tayeb_reward_eligible === 'true') {
      return true;
    }

    if (!this.eligibleProductTypes.length) {
      return true;
    }

    return this.eligibleProductTypes.indexOf((item.product_type || '').trim().toLowerCase()) !== -1;
  };

  TayebRewardProgress.prototype.getEligibleQuantity = function (cart) {
    var self = this;
    return (cart.items || []).reduce(function (total, item) {
      return total + (self.isEligible(item) ? Number(item.quantity || 0) : 0);
    }, 0);
  };

  TayebRewardProgress.prototype.refresh = function () {
    var self = this;
    window.clearTimeout(refreshTimer);
    // Use requestIdleCallback for non-critical cart updates
    if (window.requestIdleCallback) {
      window.requestIdleCallback(function () {
        refreshTimer = window.setTimeout(function () {
          self.readCart();
        }, 30);
      }, { timeout: 1000 });
    } else {
      refreshTimer = window.setTimeout(function () {
        self.readCart();
      }, 30);
    }
  };

  TayebRewardProgress.prototype.readCart = function () {
    var self = this;
    this.classList.add('is-loading');

    fetch(cartUrl('cart.js') + '?tayeb_reward=' + Date.now(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache'
      }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to read cart');
        return response.json();
      })
      .then(function (cart) {
        self.render(self.getEligibleQuantity(cart));
      })
      .catch(function () {
        self.classList.add('has-cart-error');
      })
      .finally(function () {
        self.classList.remove('is-loading');
      });
  };

  TayebRewardProgress.prototype.render = function (quantity) {
    var cappedQuantity = Math.min(quantity, this.target);
    var remaining = Math.max(this.target - quantity, 0);
    var unlocked = quantity >= this.target;
    var percent = (cappedQuantity / this.target) * 100;

    this.fillElement.style.width = percent + '%';
    this.progressElement.setAttribute('aria-valuenow', String(cappedQuantity));
    this.progressElement.setAttribute('aria-valuetext', this.progressText(cappedQuantity, remaining));
    this.classList.toggle('is-unlocked', unlocked);
    this.giftElement.classList.toggle('is-unlocked', unlocked);
    this.ctaElement.hidden = !unlocked;

    this.steps.forEach(function (step) {
      var stepNumber = Number(step.dataset.rewardStep);
      step.classList.toggle('is-active', stepNumber <= cappedQuantity);
    });

    if (unlocked) {
      this.titleElement.textContent = this.isArabic ? 'مبروك، هديتك جاهزة!' : 'Congratulations, your gift is ready!';
      this.messageElement.textContent = this.isArabic
        ? 'لقد أكملت العرض. اختر الآن عطرك المجاني.'
        : 'You completed the offer. Choose your free perfume now.';
      return;
    }

    if (quantity === 0) {
      this.titleElement.textContent = this.isArabic ? 'اقتربت هديتك!' : 'Your gift is getting closer!';
      this.messageElement.innerHTML = this.isArabic
        ? 'أضف <strong>' + this.target + ' عطور</strong> واحصل على عطر مجاناً'
        : 'Add <strong>' + this.target + ' perfumes</strong> and get 1 perfume free';
      return;
    }

    this.titleElement.textContent = this.isArabic ? 'أنت أقرب إلى هديتك!' : 'You are closer to your gift!';
    this.messageElement.innerHTML = this.remainingMessage(remaining);
  };

  TayebRewardProgress.prototype.remainingMessage = function (remaining) {
    var safeRemaining = escapeHtml(remaining);

    if (!this.isArabic) {
      return 'Only <strong>' + safeRemaining + '</strong> ' +
        (remaining === 1 ? 'perfume' : 'perfumes') +
        ' left to unlock your free gift';
    }

    if (remaining === 1) {
      return 'متبقي <strong>عطر واحد</strong> لتحصل على عطرك المجاني';
    }

    if (remaining === 2) {
      return 'متبقي <strong>عطران</strong> لتحصل على عطرك المجاني';
    }

    return 'متبقي <strong>' + safeRemaining + ' عطور</strong> لتحصل على عطرك المجاني';
  };

  TayebRewardProgress.prototype.progressText = function (quantity, remaining) {
    if (remaining === 0) {
      return this.isArabic ? 'تم فتح الهدية المجانية' : 'Free gift unlocked';
    }

    return this.isArabic
      ? quantity + ' من ' + this.target + ' عطور، متبقي ' + remaining
      : quantity + ' of ' + this.target + ' perfumes, ' + remaining + ' remaining';
  };

  if (!customElements.get('tayeb-reward-progress')) {
    customElements.define('tayeb-reward-progress', TayebRewardProgress);
  }
})();
